import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import useAutomaticBillingHoldStatus from 'src/components/document-management/use-automatic-billing-hold';
import useClosedMonths from 'src/components/document-management/use-closed-months';
import { eocTestId } from './test-ids';

type PayerInfo = {
  uuid: string;
  payerPlanId: string;
  payerType: string;
  payerName?: string;
  startDate: string;
  endDate: string | null;
  programType?: string;
  isCurrent?: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (
    endDate: string,
    closePayer: boolean,
    payerToClose: PayerInfo | null,
    payerEndDate: string | null,
    plannedDischarge: boolean
  ) => void;
  minDate: Date | null;
  maxDate: Date | null;
  allPayers: PayerInfo[];
  isMostRecentEoc?: boolean;
};

const formatDateForDisplay = (dateStr: string | null | undefined) => {
  if (!dateStr) return null;
  try {
    const date = new Date(
      new Date(dateStr).valueOf() + new Date(dateStr).getTimezoneOffset() * 60 * 1000
    );
    return format(date, 'MMM dd, yyyy');
  } catch {
    return dateStr;
  }
};

const formatDateMDY = (dateStr: string | null | undefined) => {
  if (!dateStr) return '';
  try {
    const date = new Date(
      new Date(dateStr).valueOf() + new Date(dateStr).getTimezoneOffset() * 60 * 1000
    );
    return format(date, 'MM-dd-yyyy');
  } catch {
    return dateStr;
  }
};

// THIN-8061 (epic THIN-8052, decision #10): the question says "EOC close", never "discharge" -- a DOR who
// reads "discharge" as the Discharge document answers for the wrong thing, and from THIN-8047 on the
// answer decides whether the close-day charges are held from billing. Payload and column are unchanged.
export const PLANNED_CLOSE_QUESTION = 'Was the end of this episode of care planned?';
export const PLANNED_CLOSE_HELPER =
  'Yes \u2014 the patient finished therapy or went home as planned. ' +
  'No \u2014 the episode ended unexpectedly, for example a hospital transfer.';

// THIN-8063: shown when a PLANNED close lands in a closed period at a facility where the automatic
// hold applies -- the backend then writes no hold (THIN-8047) and the person closing needs to know.
// Copy from Alex, 11 Sept, with "day of discharge" replaced by "EOC close date" per epic decision
// #10 (the noun is the close, never the Discharge document) -- the same wording as the badge in
// THIN-8050 and the question above. The "IF applicable" hedge is what lets this ship without a
// backend preview of the affected notes.
export const HOLD_ACKNOWLEDGEMENT_TITLE = 'Billing hold not applied automatically';
export const HOLD_ACKNOWLEDGEMENT_COPY =
  'A billing hold for the EOC close date, IF applicable, has NOT been automatically applied.  ' +
  'Any needed billing holds would need to be manually applied on the EOC';

const formatDateToString = (date: Date | null): string | null => {
  if (!date) return null;
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

const CloseEOCModal = ({
  open,
  onClose,
  onConfirm,
  minDate,
  maxDate,
  allPayers,
  isMostRecentEoc = true,
}: Props) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [closePayer, setClosePayer] = useState(true);
  const [payerEndDate, setPayerEndDate] = useState<Date | null>(new Date());
  // BRK-2015: 'yes' | 'no' | null — deliberately unanswered on open, the user must choose.
  const [plannedDischarge, setPlannedDischarge] = useState<string | null>(null);
  const payerEndDateManuallyEdited = useRef(false);
  // THIN-8063: the closed-period acknowledgement sits between Confirm and the actual close.
  const [showHoldAcknowledgement, setShowHoldAcknowledgement] = useState(false);
  const { appliesToFacility: automaticHoldApplies } = useAutomaticBillingHoldStatus(open);
  // Only fetched once the rule is known to apply here; reports "closed" while still loading, which
  // for an informational acknowledgement errs on the side of telling the user.
  const { isDateInClosedPeriod } = useClosedMonths(open && automaticHoldApplies === true);

  useEffect(() => {
    if (open) {
      const defaultDate = isMostRecentEoc ? new Date() : maxDate ?? new Date();
      setSelectedDate(defaultDate);
      setPayerEndDate(defaultDate);
      setClosePayer(true);
      setPlannedDischarge(null);
      setShowHoldAcknowledgement(false);
      payerEndDateManuallyEdited.current = false;
    }
  }, [open, isMostRecentEoc, maxDate]);

  const handleEocDateChange = (newDate: Date | null) => {
    setSelectedDate(newDate);
    if (!payerEndDateManuallyEdited.current) {
      setPayerEndDate(newDate);
    }
  };

  const handlePayerEndDateChange = (newDate: Date | null) => {
    payerEndDateManuallyEdited.current = true;
    setPayerEndDate(newDate);
  };

  const matchingPayer = useMemo(() => {
    if (!selectedDate || !allPayers?.length) return null;
    const dateStr = moment(selectedDate).format('YYYY-MM-DD');
    return (
      allPayers.find((p) => {
        const start = p.startDate?.split('T')[0];
        const end = p.endDate?.split('T')[0] ?? null;
        return dateStr >= start && (end === null || dateStr <= end);
      }) || null
    );
  }, [selectedDate, allPayers]);

  const payerAlreadyClosed = useMemo(() => {
    if (!matchingPayer) return true;
    return matchingPayer.endDate !== null;
  }, [matchingPayer]);

  const hasPayers = allPayers?.length > 0;

  // THIN-8063: planned close + automatic hold in force here + close date in a closed period. Unplanned
  // closes, open periods, enterprises without the opt-in and facilities outside the scope all close
  // exactly as before, with no extra dialog.
  const needsHoldAcknowledgement = () => {
    if (plannedDischarge !== 'yes' || automaticHoldApplies !== true) return false;
    const formattedEocDate = formatDateToString(selectedDate);
    return !!formattedEocDate && isDateInClosedPeriod(formattedEocDate);
  };

  const handleConfirm = () => {
    if (!selectedDate) return;
    if (plannedDischarge === null) return;
    if (needsHoldAcknowledgement()) {
      setShowHoldAcknowledgement(true);
      return;
    }
    confirmClose();
  };

  const confirmClose = () => {
    if (!selectedDate) return;
    if (plannedDischarge === null) return;
    const formattedEocDate = formatDateToString(selectedDate);
    if (!formattedEocDate) return;

    const shouldClosePayer =
      isMostRecentEoc && closePayer && !payerAlreadyClosed && !!matchingPayer;
    const formattedPayerDate = shouldClosePayer ? formatDateToString(payerEndDate) : null;
    onConfirm(
      formattedEocDate,
      shouldClosePayer,
      shouldClosePayer ? matchingPayer : null,
      formattedPayerDate,
      plannedDischarge === 'yes'
    );
  };

  const getPayerTypeDisplay = (payer: PayerInfo) => {
    return payer.payerType?.replaceAll('_', ' ') || payer.payerType;
  };

  const showKeepOpenWarning =
    isMostRecentEoc && matchingPayer && !payerAlreadyClosed && !closePayer;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid={eocTestId('close-dialog')}
    >
      <DialogTitle>
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: 1.5,
            color: '#111927',
          }}
        >
          Close Episode of Care
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Box>
            <Typography
              variant="body2"
              sx={{ color: '#6C737F', mb: 2 }}
            >
              Select the end date for this Episode of Care.
            </Typography>
            <DatePicker
              label="EOC End Date"
              slotProps={{
                textField: {
                  inputProps: { 'data-testid': eocTestId('close-end-date') },
                },
              }}
              value={selectedDate}
              onChange={handleEocDateChange}
              minDate={minDate ?? undefined}
              maxDate={maxDate ?? undefined}
              format="MMM dd, yyyy"
              sx={{ width: '100%' }}
            />
          </Box>

          {hasPayers && isMostRecentEoc && (
            <Box>
              {matchingPayer && !payerAlreadyClosed ? (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={closePayer}
                        inputProps={
                          {
                            'data-testid': eocTestId('close-payer'),
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                        onChange={(e) => setClosePayer(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: '#111927' }}
                        >
                          Close Current Payer
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: '#6C737F' }}
                        >
                          [{getPayerTypeDisplay(matchingPayer)}] - {matchingPayer.payerName || ''}{' '}
                          (Start: {formatDateForDisplay(matchingPayer.startDate) || '-'})
                        </Typography>
                      </Box>
                    }
                  />
                  {closePayer && (
                    <Box sx={{ mt: 1, ml: 4 }}>
                      <DatePicker
                        label="Payer End Date"
                        value={payerEndDate}
                        onChange={handlePayerEndDateChange}
                        format="MMM dd, yyyy"
                        sx={{
                          width: '100%',
                          ...(closePayer && !payerEndDate
                            ? {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#d32f2f',
                                },
                              }
                            : {}),
                        }}
                        slotProps={{
                          textField: {
                            inputProps: {
                              'data-testid': eocTestId('close-payer-end-date'),
                            },
                            error: closePayer && !payerEndDate,
                            helperText:
                              closePayer && !payerEndDate
                                ? 'Payer end date is required; uncheck box to keep payer open'
                                : '',
                          },
                        }}
                      />
                    </Box>
                  )}
                  {showKeepOpenWarning && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mt: 1,
                        p: 1.5,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 171, 0, 0.08)',
                      }}
                    >
                      <WarningAmberIcon sx={{ color: '#B76E00', fontSize: '20px', mt: '2px' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: '#B76E00' }}
                      >
                        Keeping payer [{getPayerTypeDisplay(matchingPayer)}] -{' '}
                        {matchingPayer.payerName || ''} open (Start Date:{' '}
                        {formatDateMDY(matchingPayer.startDate)}) will combine all future EOC data
                        in your Section O and LOS reports.
                      </Typography>
                    </Box>
                  )}
                </>
              ) : matchingPayer && payerAlreadyClosed ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked
                      disabled
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: '#9DA4AE' }}
                      >
                        Payer already closed
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#9DA4AE' }}
                      >
                        [{getPayerTypeDisplay(matchingPayer)}] - {matchingPayer.payerName || ''}{' '}
                        (Ended: {formatDateForDisplay(matchingPayer.endDate) || '-'})
                      </Typography>
                    </Box>
                  }
                  sx={{ opacity: 0.6 }}
                />
              ) : (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked
                      disabled
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: '#9DA4AE' }}
                      >
                        No active payer on this date
                      </Typography>
                    </Box>
                  }
                  sx={{ opacity: 0.6 }}
                />
              )}
            </Box>
          )}

          {/* BRK-2015: deliberately outside the hasPayers && isMostRecentEoc conditional —
              the question must be answered on every close, payer or not. */}
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#111927' }}
            >
              {PLANNED_CLOSE_QUESTION} *
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#6C737F', mb: 0.5 }}
            >
              {PLANNED_CLOSE_HELPER}
            </Typography>
            <RadioGroup
              row
              value={plannedDischarge ?? ''}
              onChange={(e) => setPlannedDischarge(e.target.value)}
            >
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500 }}
                  >
                    Yes
                  </Typography>
                }
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500 }}
                  >
                    No
                  </Typography>
                }
              />
            </RadioGroup>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px 24px 24px' }}>
        <Button
          variant="text"
          data-testid={eocTestId('close-cancel')}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          data-testid={eocTestId('close-confirm')}
          onClick={handleConfirm}
          disabled={
            !selectedDate ||
            plannedDischarge === null ||
            (closePayer && !payerAlreadyClosed && !payerEndDate) ||
            // THIN-8063: null means the gate verdict has not come back yet. Confirming in that window
            // would close the episode without ever evaluating the acknowledgement, and the backend
            // would then skip the hold silently -- the exact outcome this dialog exists to prevent.
            // Resolves to true/false in one request; a failed request resolves to false, not null.
            automaticHoldApplies === null
          }
        >
          Confirm
        </Button>
      </DialogActions>

      {/* THIN-8063: informational acknowledgement, not a block and not a choice beyond backing out. */}
      <Dialog
        open={showHoldAcknowledgement}
        onClose={() => setShowHoldAcknowledgement(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="hold-acknowledgement-title"
      >
        <DialogTitle id="hold-acknowledgement-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon sx={{ color: '#B76E00' }} />
            <Typography
              sx={{ fontSize: '20px', fontWeight: 600, lineHeight: 1.5, color: '#111927' }}
            >
              {HOLD_ACKNOWLEDGEMENT_TITLE}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#111927' }}>
            {HOLD_ACKNOWLEDGEMENT_COPY}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px 24px 24px' }}>
          <Button
            variant="text"
            onClick={() => setShowHoldAcknowledgement(false)}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowHoldAcknowledgement(false);
              confirmClose();
            }}
          >
            OK, close episode
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CloseEOCModal;
